import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Database } from "lucide-react";

const SEL = "h-8 rounded-md bg-[var(--bg-base,#0b1120)] border border-[var(--border-subtle)] px-2 text-xs text-slate-200";
const fmt = (t) => (t ? new Date(t).toISOString().slice(0, 16).replace("T", " ") : "—");

export function HistoryPanel({ sources }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ symbol: "BTCUSDT", interval: "1h", days: 90 });
  const ds = useQuery({ queryKey: ["hist-datasets"], queryFn: async () => (await api.get("/history/datasets")).data });
  const ingest = useMutation({
    mutationFn: async () => (await api.post("/history/ingest", { source: "binance", ...f, days: Number(f.days) })).data,
    onSuccess: (r) => { toast.success(`Stored ${r.accepted} candles · ${r.rejected_count} rejected · ${r.quality.gap_count} gaps`); qc.invalidateQueries({ queryKey: ["hist-datasets"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const bn = sources?.binance;
  return (
    <Panel title="Historical data (validated, real only)" testId="history-panel" actions={<Database className="w-4 h-4 text-slate-500" />}>
      <div className="p-4 space-y-3 text-xs">
        <div className="flex flex-wrap items-end gap-2">
          <select data-testid="hist-symbol" className={SEL} value={f.symbol} onChange={(e) => setF({ ...f, symbol: e.target.value })}>
            {(bn?.symbols || []).map((s) => <option key={s}>{s}</option>)}
          </select>
          <select data-testid="hist-interval" className={SEL} value={f.interval} onChange={(e) => setF({ ...f, interval: e.target.value })}>
            {(bn?.intervals || []).map((s) => <option key={s}>{s}</option>)}
          </select>
          <input data-testid="hist-days" type="number" min={1} max={3650} className={`${SEL} w-20`} value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} />
          <span className="text-slate-500">days</span>
          <Button data-testid="hist-ingest-btn" size="sm" onClick={() => ingest.mutate()} disabled={ingest.isPending}>{ingest.isPending ? "Fetching…" : "Ingest Binance history"}</Button>
        </div>
        <div data-testid="hist-sharekhan-status" className="text-amber-300/80">Sharekhan: {sources?.sharekhan?.reason}</div>
        {ds.isLoading ? <div className="text-slate-500">Loading datasets…</div> : <DatasetTable items={ds.data?.items || []} />}
      </div>
    </Panel>
  );
}

function DatasetTable({ items }) {
  if (!items.length) return <div data-testid="hist-empty" className="text-slate-500">No history stored yet.</div>;
  return (
    <div className="overflow-x-auto"><table className="w-full" data-testid="hist-table">
      <thead><tr className="text-slate-500 text-left"><th className="py-1 pr-3">Dataset</th><th className="pr-3">Candles</th><th className="pr-3">Range (UTC)</th><th className="pr-3">Complete</th><th className="pr-3">Gaps</th><th>Status</th></tr></thead>
      <tbody>{items.map((d) => (
        <tr key={`${d.source}-${d.symbol}-${d.interval}`} className="border-t border-[var(--border-subtle)] text-slate-300" data-testid={`hist-row-${d.symbol}-${d.interval}`}>
          <td className="py-1.5 pr-3 font-mono">{d.source} {d.symbol} {d.interval}</td><td className="pr-3">{d.count}</td>
          <td className="pr-3 font-mono text-slate-500">{fmt(d.first_t)} → {fmt(d.last_t)}</td><td className="pr-3">{d.completeness_pct}%</td>
          <td className="pr-3">{d.gap_count} ({d.missing} missing)</td>
          <td className={d.status === "ok" ? "text-emerald-400" : "text-amber-400"}>{d.status}</td>
        </tr>))}</tbody>
    </table></div>
  );
}
